
import { defineComponent, PropType, computed, reactive, onBeforeMount } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// types
import {
    LoadCapacity,
    LoadStandard,
    LoadStandardsPayload,
    LoadStandardsUpdatePayload,
} from '@/new-services/types/tms'
import { CityT } from '@/core/types/cities.types'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

// services
import tmsAPI from '@/new-services/tms.service'

// composable
import useNotifications from '@/common/composable/useNotifications'

// validations
import useVuelidate from '@vuelidate/core'
import { decimal, required } from '@vuelidate/validators'

const TRANSPORTATION_TYPES = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]

export default defineComponent({
    components: {
        Modal,
        TheSelect,
        TheButton,
        TheInput,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<LoadStandard>,
            default: null,
        },
    },
    emits: ['close', 'created', 'updated'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            //
            sender_city: null as number | null,
            receiver_city: null as number | null,
            transit_cities: [] as Array<number | null>,
            //
            weightLoadStandard_1: null as string | number | null,
            cubatureLoadStandard_1: null as string | number | null,
            //
            weightLoadStandard_2: null as string | number | null,
            cubatureLoadStandard_2: null as string | number | null,
            //
            weightLoadStandard_3: null as string | number | null,
            cubatureLoadStandard_3: null as string | number | null,
            //
            weightLoadStandard_5: null as string | number | null,
            cubatureLoadStandard_5: null as string | number | null,
            //
            weightLoadStandard_6: null as string | number | null,
            cubatureLoadStandard_6: null as string | number | null,
        })

        const state_form$ = useVuelidate(
            {
                sender_city: { required },
                receiver_city: { required },
                //
                weightLoadStandard_1: { decimal },
                cubatureLoadStandard_1: { decimal },
                //
                weightLoadStandard_2: { decimal },
                cubatureLoadStandard_2: { decimal },
                //
                weightLoadStandard_3: { decimal },
                cubatureLoadStandard_3: { decimal },
                //
                weightLoadStandard_5: { decimal },
                cubatureLoadStandard_5: { decimal },
                //
                weightLoadStandard_6: { decimal },
                cubatureLoadStandard_6: { decimal },
            },
            state
        )

        const cities_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CityT[],
        })

        const loadCities = async () => {
            try {
                cities_state.is_loading = true

                const response = await cities.getAllList(1, undefined, 9999)
                cities_state.list = response.data

                cities_state.is_failed = false
            } catch (error) {
                console.error(error)
                cities_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                cities_state.is_loading = false
            }
        }

        const addTransitCity = () => {
            state.transit_cities.push(null)
        }

        const deleteTransitCity = (transit_city_index: number) => {
            const answer = confirm(
                `Вы действительно хотите удалить транзитный город №${transit_city_index + 1}?`
            )
            if (!answer) return

            state.transit_cities.splice(transit_city_index, 1)
        }

        const onCancel = () => {
            if (state.is_loading) return
            emit('close')
        }

        const createLoadStandards = async () => {
            const cityIds = [...state.transit_cities]
            cityIds.unshift(state.sender_city)
            cityIds.push(state.receiver_city)

            const capacities = []

            for (let i = 1; i <= 6; i++) {
                if (i !== 4) {
                    const weightKey = `weightLoadStandard_${i}` as keyof typeof state
                    const cubatureKey = `cubatureLoadStandard_${i}` as keyof typeof state

                    capacities.push({
                        id: i,
                        weightLoadStandard: Number(state[weightKey]),
                        cubatureLoadStandard: Number(state[cubatureKey]),
                    })
                }
            }

            try {
                state.is_loading = true

                const payload: LoadStandardsPayload = {
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    capacities,
                }

                await tmsAPI.createLoadStandard(payload)

                emit('created')

                notifications.notify({
                    type: 'success',
                    title: 'Нормы загруженности созданы',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании норм загруженности произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const updateLoadStandards = async () => {
            const cityIds = [...state.transit_cities]
            cityIds.unshift(state.sender_city)
            cityIds.push(state.receiver_city)

            const capacities = []

            for (let i = 1; i <= 6; i++) {
                if (i !== 4) {
                    const weightKey = `weightLoadStandard_${i}` as keyof typeof state
                    const cubatureKey = `cubatureLoadStandard_${i}` as keyof typeof state

                    capacities.push({
                        id: i,
                        weightLoadStandard: Number(state[weightKey]),
                        cubatureLoadStandard: Number(state[cubatureKey]),
                    })
                }
            }

            try {
                state.is_loading = true

                const payload: LoadStandardsUpdatePayload = {
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    capacities,
                }

                await tmsAPI.updateLoadStandards(props.initialValue.id, payload)

                emit('updated')

                notifications.notify({
                    type: 'success',
                    title: 'Нормы загруженности сохранены',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении норм загруженности произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onConfirm = () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                createLoadStandards()
                return
            }

            if (props.isUpdate) {
                updateLoadStandards()
                return
            }
        }

        onBeforeMount(async () => {
            await loadCities()

            if (props.isUpdate && props.initialValue) {
                const cityIds = [...props.initialValue.cityIds]

                state.sender_city = cityIds[0]
                state.receiver_city = cityIds[cityIds.length - 1]

                cityIds.splice(0, 1)
                cityIds.splice(cityIds.length - 1, 1)

                if (cityIds.length) {
                    state.transit_cities = cityIds
                }

                if (props.initialValue.capacityData.length) {
                    props.initialValue.capacityData.forEach((capacity: LoadCapacity) => {
                        switch (capacity.id) {
                            case 1:
                                state.weightLoadStandard_1 = capacity.weightLoadStandard
                                state.cubatureLoadStandard_1 = capacity.cubatureLoadStandard
                                break

                            case 2:
                                state.weightLoadStandard_2 = capacity.weightLoadStandard
                                state.cubatureLoadStandard_2 = capacity.cubatureLoadStandard
                                break

                            case 3:
                                state.weightLoadStandard_3 = capacity.weightLoadStandard
                                state.cubatureLoadStandard_3 = capacity.cubatureLoadStandard
                                break

                            case 5:
                                state.weightLoadStandard_5 = capacity.weightLoadStandard
                                state.cubatureLoadStandard_5 = capacity.cubatureLoadStandard
                                break

                            case 6:
                                state.weightLoadStandard_6 = capacity.weightLoadStandard
                                state.cubatureLoadStandard_6 = capacity.cubatureLoadStandard
                                break
                        }
                    })
                }
            }
        })

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Создать'
            if (props.isUpdate) return 'Редактировать'
            return null
        })

        const confirmButtonText = computed(() => {
            if (props.isCreate) return 'Создать'
            if (props.isUpdate) return 'Сохранить'
            return 'Подтвердить'
        })

        return {
            state,
            state_form$,
            cities_state,
            loadCities,
            addTransitCity,
            deleteTransitCity,
            onCancel,
            modalTitle,
            confirmButtonText,
            onConfirm,
            TRANSPORTATION_TYPES,
        }
    },
})
